import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../../assets/images/logo.png';
import { Toast } from 'primereact/toast';
import { useAuth } from '../components/use-auth';
import { useMountEffect } from 'primereact/hooks';
// import '../../../assets/styles/auth-tokens.css';
// import '../../../assets/styles/landing.css';

const Signup = () => {

  const { logout } = useAuth();
  useMountEffect(() => {
    logout();
  });

  const toast = useRef(null);

  return (
    <>
      <Toast ref={toast} />
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-5xl">
          <div className="text-center mb-10 flex flex-col items-center">
            <Link to={'/'} className="flex items-center justify-center space-x-3 mb-6">
              <img className="h-10 w-auto" src={logo} alt="Doves" />
              <span className="bg-[#EBF5FF] text-[#135A9C] font-black text-[10px] px-3 py-1 rounded-full uppercase tracking-widest">Self-Service Portal</span>
            </Link>
            <h1 className="text-3xl font-extrabold text-slate-800 mt-2 tracking-tight">Welcome to Doves Self Service Portal</h1>
            <p className="mt-3 text-sm text-slate-500 max-w-lg mx-auto">
              Choose an option below to log in, link an existing policy, or create a new profile.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row lg:flex-wrap items-stretch gap-6 relative px-4">

            {/* 1. Left Box (Log In): 1st on Mobile, 1st on Desktop */}
            <div className="order-1 lg:order-1 lg:w-[30%] bg-[#F0F7FF] rounded-[2rem] p-8 sm:p-10 flex flex-col justify-center items-center text-center shadow-md border border-blue-50">
              <div className="w-12 h-12 bg-white text-[#135A9C] rounded-full flex items-center justify-center text-2xl mb-6 shadow-sm">
                <i className="icofont-check"></i>
              </div>
              <h2 className="text-xl font-extrabold text-slate-800 mb-3">Already have a profile?</h2>
              <p className="text-sm text-slate-500 mb-8">Log in to manage your policy, claims and details.</p>
              <Link to="/signin" className="btn bg-[#135A9C] hover:bg-[#0f4b82] text-white border-none btn-wide rounded-full font-bold shadow-md">
                Log In <i className="icofont-arrow-right ml-1"></i>
              </Link>
            </div>

            {/* 2. Quick Pay Banner: 2nd on Mobile (right below Log In), 4th (full-width bottom row) on Desktop */}
            <div className="order-2 lg:order-4 w-full lg:mt-2">
              <div className="bg-white border-2 border-[#135A9C]/20 rounded-[2rem] p-5 sm:px-8 sm:py-6 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4 text-center sm:text-left flex-col sm:flex-row">
                  <div className="w-12 h-12 rounded-full bg-[#F0F7FF] text-[#135A9C] flex items-center justify-center text-2xl shrink-0 shadow-sm">
                    <i className="icofont-credit-card"></i>
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-extrabold text-slate-800">
                      Just need to make a payment?
                    </h2>
                    <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                      Quickly pay towards your policy without signing in.
                    </p>
                  </div>
                </div>
                <Link
                  to="/pay-guest"
                  className="btn bg-[#135A9C] hover:bg-[#0f4b82] text-white border-none rounded-full font-bold px-6 w-full sm:w-auto shrink-0 shadow-md"
                >
                  Pay without signing in <i className="icofont-arrow-right ml-1"></i>
                </Link>
              </div>
            </div>

            {/* 3. Divider: 3rd on Mobile, 2nd on Desktop */}
            <div className="order-3 lg:order-2 divider lg:divider-horizontal">
              <div className="bg-white border border-slate-200 text-slate-400 text-xs font-bold px-2 py-1 rounded-full shadow-sm">
                OR
              </div>
            </div>

            {/* 4. Right Box (Create Profile): 4th on Mobile, 3rd on Desktop */}
            <div className="order-4 lg:order-3 lg:flex-1 bg-white border border-slate-200 rounded-[2rem] p-8 sm:p-10 shadow-sm flex flex-col">
              <div className="mb-8 text-center">
                <h2 className="text-xl font-extrabold text-slate-800 mb-2">Create a new profile</h2>
                <p className="text-sm text-slate-500">New to Doves? Pick the option that matches you.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 flex-grow">
                {/* Option 1 */}
                <div className="bg-white rounded-3xl p-6 border border-slate-200 hover:border-[#135A9C]/30 hover:shadow-sm transition-all group flex flex-col h-full items-center text-center">
                  <div className="w-12 h-12 bg-[#F0F7FF] text-[#135A9C] rounded-full flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform">
                    <i className="icofont-id-card"></i>
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">I have <strong className="text-[#135A9C]">an existing</strong> policy</h3>
                  <p className="text-xs text-slate-500 mb-6 flex-grow">Link your policy number and National ID to activate self-service access.</p>
                  <Link to="/register-policyholder" className="btn btn-outline border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-800 hover:border-slate-300 btn-sm rounded-full font-bold px-6">
                    Link Policy <i className="icofont-arrow-right ml-1 text-[#135A9C]"></i>
                  </Link>
                </div>

                {/* Option 2 */}
                <div className="bg-white rounded-3xl p-6 border border-slate-200 hover:border-[#135A9C]/30 hover:shadow-sm transition-all group flex flex-col h-full items-center text-center">
                  <div className="w-12 h-12 bg-[#F0F7FF] text-[#135A9C] rounded-full flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform">
                    <i className="icofont-plus"></i>
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">I <strong className="text-[#135A9C]">do not</strong> have a policy</h3>
                  <p className="text-xs text-slate-500 mb-6 flex-grow">Create a brand new profile to get started with Doves.</p>
                  <Link to="/create-new-account" className="btn btn-outline border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-800 hover:border-slate-300 btn-sm rounded-full font-bold px-6">
                    Create New Profile <i className="icofont-arrow-right ml-1 text-[#135A9C]"></i>
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default Signup;
